import { useEffect, useState } from 'react';

import IconFolder from '@/assets/svg/icon-folder.svg?react';

import { cn } from '@/lib/utils';

import { useSearchStore } from '@/stores/searchStore';

import { ExplorerItemType } from '@/constants/table-columns-config';

interface SearchComponentAutosuggestProps {
  setCurrentUUID: (uuid: string) => void;
  setCurrentEntityType: (entityType: ExplorerItemType) => void;
  onNavigate?: (params: { uuid: string; entityType: ExplorerItemType }) => void;
}

const SearchComponentAutosuggest = ({
  setCurrentUUID,
  setCurrentEntityType,
  onNavigate,
}: SearchComponentAutosuggestProps) => {
  const [query, setQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<{ label: string; value: string }[]>(
    [],
  );

  const { getAgreementsByMask } = useSearchStore();

  // Simple debounce to avoid flooding the backend
  useEffect(() => {
    if (!query || query.trim().length === 0) {
      setOptions([]);
      return;
    }
    const handle = setTimeout(async () => {
      setIsLoading(true);
      try {
        const res = await getAgreementsByMask(query.trim());
        setOptions(res);
      } finally {
        setIsLoading(false);
      }
    }, 250);
    return () => clearTimeout(handle);
  }, [query, getAgreementsByMask]);

  const handleSelect = (uuid: string) => {
    // Jump straight to FILE level for the selected agreement
    setCurrentUUID(uuid);
    setCurrentEntityType(ExplorerItemType.FILE);
    onNavigate?.({ uuid, entityType: ExplorerItemType.FILE });
  };

  return (
    <div className={cn('relative py-1')}>
      <input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 100)}
        placeholder="Пошук договорів: ІНДЕКС_ДНЗ_НАЗВА"
        className={cn('h-14 w-full border-b px-9 py-3 focus:outline-none')}
      />

      {open && (query.length > 0 || isLoading) && (
        <div className="absolute left-0 right-0 top-[calc(100%)] z-30 overflow-hidden rounded-md border border-neutral-200 bg-white shadow-lg dark:border-neutral-800 dark:bg-neutral-950">
          {isLoading ? (
            <div className="px-4 py-3 text-sm text-gray-400">Завантаження…</div>
          ) : options.length === 0 ? (
            <div className="px-4 py-3 text-sm text-gray-400">
              Нічого не знайдено
            </div>
          ) : (
            <ul className="max-h-[360px] overflow-auto">
              {options.map((opt) => (
                <li
                  key={opt.value}
                  className="flex cursor-pointer items-center gap-3 border-b border-neutral-100 px-4 py-3 last:border-b-0 hover:bg-neutral-50 dark:border-neutral-800 dark:hover:bg-neutral-900"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => handleSelect(opt.value)}
                  title={opt.label}
                >
                  <IconFolder className="h-6 w-6 min-w-6 text-primary" />
                  <span className="truncate text-sm font-medium text-primary">
                    {opt.label}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchComponentAutosuggest;
